import dayjs from 'dayjs';
import {MRT_ColumnDef} from 'material-react-table';
import React, {RefObject, useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useShallow} from 'zustand/react/shallow';
import {Tabs} from '@lightricks/react-design-system';
import {TabData} from '@lightricks/react-design-system/dist/components/tabs/TabsProps';
import AnalyticsService, {
  eventNames,
} from '@/services/analytics/AnalyticsService';
import Sentry from '@/services/sentry/Sentry';
import {FLOW_NAMES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {
  type CreatorBrandSafetyVetting,
  type CompletedCreatorBrandSafetyVettingStatus,
  type VettingsTabCounts,
} from '@/types/creatorBrandSafetyVetting';
import SearchInput from '@/components/search-input';
import Table from '@/components/table';
import useBrandId from '@/hooks/use-brand-id';
import useElementDimensions from '@/hooks/use-element-dimensions';
import useNavigation from '@/hooks/use-navigation';
import useEmberIframeStyleStore from '@/stores/emberIframeStyleStore';
import useVettingHubStore, {
  TAB_VALUES,
  vettingHubActions,
} from '@/stores/vettingHubStore';
import trackBrandSafetyDetailEvent from '../../utils/vettingHubAnalytics';
import styles from './VettingsTable.module.scss';
import {
  CreatorCell,
  LastUpdatedCell,
  DateAddedCell,
  FlagsCell,
  StatusCell,
} from './cells';
import {COMPLETED_REPORT_STATUSES, PLACEHOLDER_ID} from './constants';

const MAX_STICKY_TABLE_WIDTH = 1060;

type VettingsTableProps = {
  testID?: string;
  data: CreatorBrandSafetyVetting[];
  isLoading?: boolean;
  tabCounts?: VettingsTabCounts;
  onEndReached?: () => void;
  isFetchingNextPage?: boolean;
  totalCount?: number;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
};

const sendTableDataPresentedEvent = (
  data: CreatorBrandSafetyVetting[],
  tabValue: string
) => {
  try {
    const flow = AnalyticsService.getOrCreateFlow(
      FLOW_NAMES.BRAND_SAFETY_CREATOR
    );
    const creatorIds = data.map((creator) => ({
      creator_id: creator.id,
      creator_name: creator.creatorName,
      status: creator.status,
      date_added: creator.createdAt ? dayjs(creator.createdAt).valueOf() : null,
      last_updated: creator.reportUpdatedAt
        ? dayjs(creator.reportUpdatedAt).valueOf()
        : null,
      flags: creator.itemCount,
    }));
    const payload = {
      flow_id: flow.flow_id,
      screen_presentation_id: flow.screen_presentation_id,
      tab: tabValue,
      brand_safety_screen_name: 'Hub',
      creator_id: creatorIds,
    };

    AnalyticsService.dispatchEvent(
      eventNames.BRAND_SAFETY_RESULTS_PRESENTED,
      payload
    );
  } catch (error) {
    Sentry.captureException(Error('Error dispatching event', {cause: error}));
  }
};

function VettingsTable(props: VettingsTableProps) {
  const {
    testID = 'vettings-table',
    data,
    isLoading,
    tabCounts,
    onEndReached,
    isFetchingNextPage,
    totalCount,
    searchValue,
    onSearchChange,
  } = props;
  const {navigate} = useNavigation();
  const brandId = useBrandId();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, sorting] = useVettingHubStore(
    useShallow((state) => [state.activeTab, state.sorting])
  );

  useEffect(() => {
    const sortingColumn = searchParams.get('sortingColumn');
    const sortingDirection = searchParams.get('sortingDir');

    if (sortingColumn && sortingDirection) {
      vettingHubActions.setSorting([
        {desc: sortingDirection === 'desc', id: sortingColumn},
      ]);
    } else {
      searchParams.delete('sortingColumn');
      searchParams.delete('sortingDir');
      setSearchParams(searchParams);
    }
  }, []);

  const appendTabCounts = React.useCallback(
    (tab: TabData) => {
      if (isLoading || !tabCounts) return tab;

      const countKey = tab.value as keyof VettingsTabCounts;
      const count = tabCounts[countKey] ?? 0;
      return {
        ...tab,
        label: `${tab.label} (${count})`,
      };
    },
    [tabCounts, isLoading]
  );

  const tabsData: TabData[] = [
    {label: translate('all'), value: TAB_VALUES.ALL},
    {label: translate('Ready for review'), value: TAB_VALUES.READY_FOR_REVIEW},
    {label: translate('Approved'), value: TAB_VALUES.APPROVED},
    {label: translate('rejected'), value: TAB_VALUES.DECLINED},
    {label: translate('queued'), value: TAB_VALUES.INVITED},
    {
      label: translate('vetting-unavailable'),
      value: TAB_VALUES.VETTING_UNAVAILABLE,
    },
    {label: translate('Report Failed'), value: TAB_VALUES.REPORT_FAILED},
  ];

  const tabs = tabsData.map(appendTabCounts);

  const columnAlignment = (align: 'left' | 'center' | 'right') => ({
    muiTableHeadCellProps: {align},
    muiTableBodyCellProps: {align},
  });

  const columnSizes = {size: 100, grow: 1};

  const tableColumns: MRT_ColumnDef<CreatorBrandSafetyVetting>[] = [
    {
      header: translate('creator-name'),
      Header: translate('creator').toUpperCase(),
      Cell: CreatorCell,
      accessorKey: 'creatorName',
      enableSorting: true,
      ...columnSizes,
      size: 200,
      grow: 0,
      ...columnAlignment('left'),
    },
    {
      header: translate('last-updated'),
      Header: translate('last-updated').toUpperCase(),
      Cell: LastUpdatedCell,
      accessorKey: 'reportUpdatedAt',
      enableSorting: true,
      ...columnSizes,
      ...columnAlignment('center'),
    },
    {
      header: translate('added'),
      Header: translate('added').toUpperCase(),
      Cell: DateAddedCell,
      accessorKey: 'createdAt',
      enableSorting: true,
      ...columnSizes,
      ...columnAlignment('center'),
    },
    {
      header: translate('flags'),
      Header: translate('flags').toUpperCase(),
      Cell: FlagsCell,
      accessorKey: 'itemCount',
      enableSorting: true,
      ...columnSizes,
      ...columnAlignment('center'),
    },
    {
      header: translate('status'),
      Header: translate('status').toUpperCase(),
      Cell: StatusCell,
      accessorKey: 'status',
      enableSorting: true,
      ...columnSizes,
      ...columnAlignment('center'),
    },
  ];

  const isSearchActive = !!searchValue;

  const renderData = React.useMemo(() => {
    if (isLoading) return [];

    if (data.length === 0 && tabValue === TAB_VALUES.ALL && !isSearchActive) {
      sendTableDataPresentedEvent([], tabValue);
      return [placeholderRow];
    }

    sendTableDataPresentedEvent(data, tabValue);
    return data;
  }, [data, isLoading, tabValue, isSearchActive]);

  const {sidebarWidth} = useEmberIframeStyleStore();

  const {elementRef: tableRef, elementWidth: tableWidth} = useElementDimensions(
    [renderData, sidebarWidth]
  );

  const isRowDisabled = (row: CreatorBrandSafetyVetting) =>
    row.id === PLACEHOLDER_ID ||
    row.postCount === 0 ||
    !COMPLETED_REPORT_STATUSES.includes(
      row.status as CompletedCreatorBrandSafetyVettingStatus
    );

  const onRowClick = (row: CreatorBrandSafetyVetting) => {
    vettingHubActions.setHoveredRow(null);
    const redirectUrl = `/vetting/creators/${row.profileId}/report/${row.reportId}?brandId=${brandId}&from=vettingHub`;
    navigate(redirectUrl);
  };

  return (
    <div
      className={`${styles.container} ${
        tableWidth < MAX_STICKY_TABLE_WIDTH ? styles.nonStickyTable : ''
      }`}
      data-testid={testID}
    >
      {onSearchChange && (
        <div className={styles.searchContainer}>
          <SearchInput
            value={searchValue}
            onChange={onSearchChange}
            onSubmit={onSearchChange}
            placeholder={translate('Search by creator name')}
          />
          {isSearchActive && !isLoading && (
            <span
              className={styles.resultCount}
              data-testid={`${testID}-result-count`}
            >
              {translate('search-result-count', {
                found: tabCounts?.all ?? 0,
                total: totalCount ?? 0,
              })}
            </span>
          )}
        </div>
      )}
      <Tabs
        testID={`${testID}-tabs`}
        ScrollButtonComponent="div"
        className={styles.tabsContainer}
        size="large"
        appearance="neutral"
        onChange={(_e, value) => {
          vettingHubActions.setActiveTab(value);
        }}
        value={tabValue}
        classes={{root: styles.tabs}}
        tabsData={tabs}
        big
      />
      <Table
        tableRef={tableRef as RefObject<HTMLTableElement>}
        testID={`${testID}-table`}
        classes={{
          container: undefined,
        }}
        columns={tableColumns}
        data={renderData}
        isLoadingInitial={isLoading}
        showProgressBars={false}
        visibleColumns={{}}
        rowSelection={{}}
        isRowDisabled={isRowDisabled}
        sorting={sorting}
        onSorting={(value) => {
          trackBrandSafetyDetailEvent({
            brand_safety_detail_name: 'Sorting',
            brand_safety_detail_value: JSON.stringify(value),
          });
          vettingHubActions.setSorting(value);
          const [sort] = value;
          if (sort) {
            searchParams.set('sortingColumn', sort.id);
            searchParams.set('sortingDir', sort.desc ? 'desc' : 'asc');
          } else {
            searchParams.delete('sortingColumn');
            searchParams.delete('sortingDir');
          }
          setSearchParams(searchParams);
        }}
        isLoading={isLoading || isFetchingNextPage}
        enableVirtualization
        enableSorting
        manualSorting
        onEndReached={onEndReached}
        onRowClick={onRowClick}
        onRowHover={vettingHubActions.setHoveredRow}
      />
    </div>
  );
}

const placeholderRow: CreatorBrandSafetyVetting = {
  id: PLACEHOLDER_ID,
  modashId: null,
  type: 'creator_brand_safety_vettings',
  reportId: '',
  status: 'ready_for_review',
  creatorName: 'Your First Creator',
  createdAt: dayjs().subtract(8, 'day').toISOString(),
  reportUpdatedAt: dayjs().subtract(7, 'day').toISOString(),
  reportStatus: 'ready',
  itemCount: 128,
  postCount: 1024,
  optedOut: false,
  profileId: '',
};

export default VettingsTable;
